/**
 * Customer Portal skeleton loaders — UX Phase A. Fixes the direct
 * `UI_UX_STANDARDS.md` violation flagged by the redesign blueprint (F3):
 * every Customer Portal loading state used a bare spinner, never a skeleton.
 * Deliberately forked from the shared staff `Skeleton*` components (which
 * read the staff `.dark`/hsl(var(--muted)) theme) rather than reused —
 * `.customer-shimmer` reads only `--customer-border`, scoped under
 * `#root.customer-portal-root`, mirroring the same fork rationale already
 * established for CustomerSearchBar/CustomerPagination.
 *
 * Premium UI pass: dimensions track the redesigned layouts (compact stat
 * tiles, hero + banner + sections on job detail) so there is no layout shift
 * when content arrives.
 */
function Block({ className = '' }: { className?: string }) {
  return <div className={`customer-shimmer rounded-md ${className}`} aria-hidden="true" />;
}

export function CustomerSkeletonCard({ className = '' }: { className?: string }) {
  return (
    <div
      className={`flex min-h-[6.25rem] flex-col justify-between gap-3 rounded-customer border border-customer-border bg-customer-surface p-4 ${className}`}
    >
      <Block className="h-9 w-9 rounded-customer-sm" />
      <div className="space-y-2">
        <Block className="h-6 w-1/3" />
        <Block className="h-3 w-2/3" />
      </div>
    </div>
  );
}

export function CustomerSkeletonList({ rows = 4 }: { rows?: number }) {
  return (
    <div className="rounded-customer border border-customer-border bg-customer-surface p-4 sm:p-5">
      <Block className="mb-4 h-4 w-32" />
      <div className="space-y-3">
        {Array.from({ length: rows }, (_, i) => (
          <div
            key={i}
            className="flex items-center justify-between border-b border-customer-border py-2.5 last:border-0"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <Block className="h-3.5 w-1/2" />
              <Block className="h-3 w-1/4" />
            </div>
            <Block className="h-5 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Detail-page loading state — hero + status strip + two sections. */
export function CustomerSkeletonDetail() {
  return (
    <div className="customer-fade-in space-y-4" aria-busy="true" aria-label="Loading details">
      <div className="rounded-customer border border-customer-border bg-customer-surface p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <Block className="h-7 w-40" />
          <Block className="h-5 w-24 rounded-full" />
        </div>
        <Block className="mt-3 h-4 w-56" />
        <Block className="mt-2 h-3 w-72 max-w-full" />
      </div>
      <Block className="h-16 w-full rounded-customer" />
      <div className="grid gap-4 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="rounded-customer border border-customer-border bg-customer-surface p-4 sm:p-5"
          >
            <Block className="mb-4 h-4 w-32" />
            <div className="space-y-3">
              <Block className="h-3.5 w-full" />
              <Block className="h-3.5 w-5/6" />
              <Block className="h-3.5 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Compact loading state for modal/panel content (e.g. Authorization Case detail). */
export function CustomerSkeletonModal() {
  return (
    <div className="space-y-4 py-2" aria-busy="true" aria-label="Loading">
      <Block className="h-5 w-32 rounded-full" />
      <div className="space-y-2">
        <Block className="h-3.5 w-1/3" />
        <Block className="h-3.5 w-full" />
        <Block className="h-3.5 w-5/6" />
      </div>
      <div className="space-y-2">
        <Block className="h-3.5 w-1/4" />
        <Block className="h-12 w-full rounded-customer-sm" />
      </div>
    </div>
  );
}

export function CustomerSkeletonDashboard() {
  return (
    <div className="customer-fade-in space-y-5" aria-busy="true" aria-label="Loading dashboard">
      <div className="rounded-customer-lg border border-customer-border bg-customer-surface p-5">
        <Block className="mb-3 h-3 w-32" />
        <Block className="mb-2 h-6 w-56 max-w-full" />
        <Block className="h-3.5 w-72 max-w-full" />
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <CustomerSkeletonCard key={i} />
        ))}
      </div>
      <CustomerSkeletonList rows={5} />
    </div>
  );
}
